import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConsulting } from '../../context/ConsultingContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { useToast } from '../../context/ToastContext';
// 포트폴리오 기능은 모든 사용자에게 오픈
import { useResumeData } from '../../hooks/useResumeData';
import { Header } from '../../components/layout';
import ResumeRenderer from '../../components/resume/ResumeRenderer';
import ResumeEditForm from '../../components/resume/ResumeEditForm';
import ResumeToolbar from '../../components/resume/ResumeToolbar';
import { ProgressIndicator, useProgress, LatteButton, LatteSpinner, ConfirmationModal } from '../../components/common';
import { OnboardingTour } from '../../components/onboarding';
import { resumeEditSteps } from '../../utils/onboardingSteps';
import { MessageCircle, Edit3, ArrowRight, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { downloadResumeAsPDF } from '../../utils/pdfGenerator';
import { PortfolioGenerator } from '../../components/portfolio';
import { useReduceTabRefresh } from '../../hooks/useReduceTabRefresh';
import { generateEnglishVersion } from '../../utils/englishVersion';
import { trackEvent } from '../../utils/analytics';

const ResumePage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { currentSession, extractedResumeData } = useConsulting();
  const { showSuccess, showError, showInfo } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 탭 전환 시 API 호출 감소
  useReduceTabRefresh();
  
  // 온보딩 관련
  const { 
    isOnboardingActive, 
    completeOnboarding, 
    skipOnboarding,
    startOnboarding,
    currentStep: onboardingStep,
    activePage,
    isOnboardingCompleted 
  } = useOnboarding();

  // 커스텀 훅으로 이력서 데이터 관리
  const {
    resumeData,
    sessionData,
    loading,
    existingPortfolio,
    portfolioLoading,
    updateResumeData,
    saveResumeData,
    isResumeComplete,
    setExistingPortfolio,
    refreshResumeData,
    englishUpdateNeeded,
    englishUpdateInfo,
    checkEnglishUpdateStatus,
    modifiedFields,
    setModifiedFields,
    setLastSyncedData
  } = useResumeData(id, user, currentSession, extractedResumeData, showError, showSuccess);

  // 로컬 상태
  const [isEditing, setIsEditing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const [isGeneratingEnglish, setIsGeneratingEnglish] = useState(false);
  const [englishGenerationError, setEnglishGenerationError] = useState(false); // 에러 상태 추가
  const [currentLanguage, setCurrentLanguage] = useState('kr'); // 'kr' 또는 'en'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null); // 선택된 템플릿 ID 추적

  
  const resumeTemplateRef = useRef(null);
  const fromConsulting = searchParams.get('from') === 'consulting';
  const templateId = searchParams.get('template');
  const shouldStartInEditMode = searchParams.get('edit') === 'true';
  const actualProgress = useProgress(resumeData);

  // URL 파라미터로 편집 모드 시작 (초기 한 번만)
  useEffect(() => {
    if (!loading && shouldStartInEditMode) {
      setIsEditing(true);
    }
  }, [loading]); // shouldStartInEditMode와 isEditing을 의존성에서 제거

  // 템플릿 ID 초기화 및 변경 추적
  useEffect(() => {
    if (templateId) {
      setSelectedTemplateId(Number(templateId));
    } else {
      setSelectedTemplateId(1); // 기본 템플릿 ID
    }
  }, [templateId]);

  // 온보딩 관련 effects (최적화)
  useEffect(() => {
    // 로딩 중이면 아무것도 안함
    if (loading) return;
    
    const shouldStartOnboarding = fromConsulting && !isOnboardingActive && !isOnboardingCompleted;
    
    if (shouldStartOnboarding) {
      setIsEditing(true);
      
      // 딘레이 최소화
      const timeoutId = setTimeout(() => {
        const targetElement = document.querySelector('[data-tour="edit-tabs"]');
        if (targetElement) {
          startOnboarding('resume-edit');
        }
      }, 500); // 딘레이 증가
      
      return () => clearTimeout(timeoutId);
    }
  }, [fromConsulting, loading, isOnboardingActive, isOnboardingCompleted, startOnboarding]);
  
  // 온보딩 활성화 시 편집 모드 설정
  useEffect(() => {
    if (isOnboardingActive && onboardingStep === 'resume-edit' && !loading) {
      setIsEditing(true);
    }
  }, [isOnboardingActive, onboardingStep, loading]);

  // 핸들러 함수들
  const handleOnboardingComplete = () => {
    if (onboardingStep === 'resume-edit') {
      completeOnboarding();
      handleContinueConsulting(true);
      return;
    }
    completeOnboarding();
  };

  // 편집 시작 시 이벤트 추적
  const handleStartEditing = () => {
    setIsEditing(true);
    trackEvent.startEditingResume(id || currentSession?.id);
  };

  const handleSkipOnboarding = () => {
    skipOnboarding();
  };

  const handleFinishEditing = async (shouldSave = true) => {
    console.log('handleFinishEditing called with shouldSave:', shouldSave);
    console.log('Current isEditing state:', isEditing);
    
    try {
      if (shouldSave) {
        await saveResumeData();
        showSuccess('이력서가 저장되었습니다!', 1000);
        
        // 수정된 필드 추적
        const editedFields = Array.from(modifiedFields);
        trackEvent.saveResumeEdit(id || currentSession?.id, editedFields);
        
        // 저장 후 영어 버전 업데이트 상태 다시 체크
        const sessionId = id || currentSession?.id;
        if (sessionId) {
          setTimeout(async () => {
            await checkEnglishUpdateStatus(sessionId);
          }, 500); // 0.5초 후 체크
        }
      } else {
        // 저장하지 않고 취소한 경우
        trackEvent.cancelResumeEdit(id || currentSession?.id);
      }
      console.log('Setting isEditing to false');
      setIsEditing(false);
    } catch (error) {
      console.error('Error in handleFinishEditing:', error);
      showError('저장 중 오류가 발생했습니다.', 1000);
      // 에러가 발생해도 편집 모드는 닫기
      setIsEditing(false);
    }
  };



  const handleContinueConsulting = (isVoiceTestMode = false) => {
    const sessionId = id || currentSession?.id;
    if (!sessionId) {
      showError('AI 컨설팅을 시작할 세션 정보가 없습니다.');
      return;
    }
    
    const url = `/consulting?session=${sessionId}${isVoiceTestMode ? '&voiceTest=true' : ''}`;
    navigate(url);
  };

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      if (!resumeData) {
        throw new Error('이력서 데이터가 없습니다.');
      }
      
      // 추적을 위한 데이터 준비
      const trackingData = {
        userId: user?.id,
        sessionId: id || currentSession?.id,
        resumeId: resumeData.id || id,
        language: currentLanguage, // 언어 정보만 추가 (추적용)
        templateId: selectedTemplateId // 선택된 템플릿 ID 추가
      };
      
      // DOM에 렌더링된 현재 언어의 이력서가 PDF로 생성됨
      const result = await downloadResumeAsPDF(resumeData, trackingData);
      
      if (result) {
        console.log(`✅ ${currentLanguage === 'en' ? '영어' : '한국어'} 버전 PDF 다운로드 프로세스가 시작되었습니다.`);
        // PDF 다운로드 이벤트 추적
        trackEvent.downloadDocument('resume', currentLanguage === 'en' ? 'pdf_en' : 'pdf_kr');
      } else {
        console.log('📝 사용자가 PDF 다운로드를 취소했습니다.');
      }
    } catch (error) {
      console.error('❌ PDF 생성 중 오류 발생:', error);
      showError(`PDF 생성 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = () => {
    const sessionId = id || currentSession?.id;
    
    if (!sessionId || !user?.id) {
      showError('삭제할 세션 정보가 없습니다.');
      return;
    }

    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    const sessionId = id || currentSession?.id;
    
    try {
      // 소프트 삭제로 처리
      const { error: deleteError, count: updatedCount } = await supabase
        .from('consulting_sessions')
        .update({ 
          deleted_at: new Date().toISOString(),
          status: 'abandoned'  // 상태도 함께 업데이트
        }, { count: 'exact' })
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .is('deleted_at', null);  // 이미 삭제된 것은 제외
        
      if (deleteError) {
        console.error('❌ 삭제 오류:', deleteError);
        showError('삭제 중 오류가 발생했습니다: ' + deleteError.message);
        return;
      }
      
      if (updatedCount === 0) {
        showError('세션 삭제에 실패했습니다. 이미 삭제되었거나 권한이 없습니다.');
        return;
      }
      
      showSuccess('이력서가 삭제되었습니다.');
      navigate('/documents');
      
    } catch (error) {
      console.error('❌ 삭제 중 예외:', error);
      showError('삭제 중 오류가 발생했습니다.');
    }
  };

  // 영어 버전 생성 핸들러
  const handleGenerateEnglishVersion = async () => {
    if (!resumeData || !id) {
      return;
    }

    setIsGeneratingEnglish(true);
    setEnglishGenerationError(false); // 에러 상태 초기화
    
    try {
      // 수정된 필드가 있으면 해당 필드만 번역, 없으면 전체 번역
      const fieldsToTranslate = modifiedFields.size > 0 ? modifiedFields : null;
      
      const englishResumeJSON = await generateEnglishVersion(
        resumeData, 
        id, 
        (message, progress) => {
          // 토스트 메시지 제거, 콘솔 로그만 유지
          console.log(`진행률: ${progress}% - ${message}`);
        },
        fieldsToTranslate
      );
      
      // 성공적으로 생성된 경우 로컬 상태 업데이트
      updateResumeData({
        ...resumeData,
        english_version: englishResumeJSON
      });
      
      // 영어 버전 생성 이벤트 추적
      trackEvent.generateEnglishResume(id || currentSession?.id);
      
      // 수정 필드 초기화 및 마지막 동기화 데이터 업데이트
      setModifiedFields(new Set());
      setLastSyncedData(resumeData);
      
      // 업데이트 상태를 확인하기 위해 checkEnglishUpdateStatus만 호출
      // (이미 로컬 상태가 업데이트되었고 modifiedFields가 초기화되었으므로 
      //  englishUpdateNeeded가 false로 설정될 것임)
      await checkEnglishUpdateStatus(id);
      
    } catch (error) {
      console.error('❌ 영어 버전 생성 실패:', error);
      
      // 파싱 에러인 경우 재시도 가능하도록 에러 상태 설정
      if (error.message.includes('영어 이력서 생성 중 오류가 발생했습니다')) {
        setEnglishGenerationError(true);
      }
      
      showError(error.message);
    } finally {
      setIsGeneratingEnglish(false);
    }
  };

  // 언어 전환 핸들러
  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    trackEvent.switchLanguage(id || currentSession?.id, language);
    
    // 영어 버전을 처음 보는 경우
    if (language === 'en' && resumeData?.english_version) {
      trackEvent.viewEnglishResume(id || currentSession?.id);
    }
  };

  if (loading) {
    return (
      <div className="page-container mobile-fixed-layout">
      <Header title="이력서 보기" />
        <div className="w-full max-w-7xl mx-auto p-6">
          <div className="card-container p-12">
          <div className="flex-col flex-center">
          <LatteSpinner size="lg" className="mb-4" />
          <p className="text-gray-600">이력서 데이터를 불러오는 중...</p>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container mobile-fixed-layout">
    <Header title="이력서 보기" />
      
      <div className="w-full px-6 pb-4">
        <ResumePageHeader 
          sessionData={sessionData}
          resumeData={resumeData}
          actualProgress={actualProgress}
          onContinueConsulting={handleContinueConsulting}
        />

        <div className={`grid ${isEditing ? 'grid-cols-2' : 'grid-cols-1'} gap-6 w-full mt-3`}>
          {isEditing && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <ResumeEditForm
                resumeData={resumeData}
                onUpdate={updateResumeData}
                onSave={saveResumeData}
                onCancel={(wasSaved) => handleFinishEditing(wasSaved)}
              />
            </div>
          )}

          <div className="w-full">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <ResumeToolbar
              resumeData={resumeData}
              sessionData={sessionData}
              isEditing={isEditing}
              isDownloading={isDownloading}
              actualProgress={actualProgress}
              existingPortfolio={existingPortfolio}
              portfolioLoading={portfolioLoading}
              onEdit={isEditing ? handleFinishEditing : handleStartEditing}
              onDownload={handleDownload}
              onContinueConsulting={handleContinueConsulting}
              onPortfolioModal={() => setIsPortfolioModalOpen(true)}
              onDelete={handleDelete}
              isOnboardingActive={isOnboardingActive}
              onboardingStep={onboardingStep}
              onGenerateEnglishVersion={handleGenerateEnglishVersion}
              isGeneratingEnglish={isGeneratingEnglish}
              englishGenerationError={englishGenerationError} // 에러 상태 전달
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              englishUpdateNeeded={englishUpdateNeeded}
              showInfo={showInfo}
              englishUpdateInfo={englishUpdateInfo}
              selectedTemplateId={selectedTemplateId}
              sessionId={id || currentSession?.id}
              userId={user?.id}
              />
              
              <div className="bg-white overflow-auto" style={{ height: 'calc(100vh - 280px)' }}>
                {currentLanguage === 'kr' ? (
                  <ResumeRenderer
                    resumeData={resumeData}
                    updatingField={null}
                    containerMode="responsive"
                    enableAutoScale={true}
                    onContainerRef={(ref) => { resumeTemplateRef.current = ref; }}
                    className="resume-template"
                    style={{ width: '100%', height: '100%' }}
                    selectedTemplate={selectedTemplateId || (templateId ? Number(templateId) : 1)}
                    onTemplateChange={setSelectedTemplateId}
                  />
                ) : (
                  <ResumeRenderer
                    resumeData={(() => {
                      const englishData = {
                        ...resumeData,
                        // 영어 데이터로 대체
                        name: resumeData?.name_en || resumeData?.english_version?.name || resumeData.name, // name_en 우선 사용
                      title: resumeData?.english_version?.title || resumeData.title,
                      summary: resumeData?.english_version?.summary || resumeData.summary,
                      keyStrengths: resumeData?.english_version?.key_strengths || resumeData.keyStrengths,
                      experiences: resumeData?.english_version?.experiences || resumeData.experiences,
                      education: resumeData?.english_version?.education || resumeData.education,
                      skills: resumeData?.english_version?.skills || resumeData.skills,
                      certificates: resumeData?.english_version?.certificates || resumeData.certificates,
                      papers: resumeData?.english_version?.papers || resumeData.papers,
                      };

                      return englishData;
                    })()}
                    updatingField={null}
                    containerMode="responsive"
                    enableAutoScale={true}
                    onContainerRef={(ref) => { resumeTemplateRef.current = ref; }}
                    className="resume-template"
                    style={{ width: '100%', height: '100%' }}
                    language="en" // 영어 버전으로 설정
                    selectedTemplate={selectedTemplateId || (templateId ? Number(templateId) : 1)}
                    onTemplateChange={setSelectedTemplateId}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* 영어 버전 이력서 영역 - 더 이상 필요없음 */}
        {/* 언어 전환은 맨 위 매인 컴포넌트에서 처리됨 */}
      </div>
      
      <OnboardingTour
        isActive={isOnboardingActive && onboardingStep === 'resume-edit' && activePage === 'resume-edit' && isEditing}
        steps={resumeEditSteps}
        onComplete={handleOnboardingComplete}
        onSkip={handleSkipOnboarding}
      />
      
      <PortfolioGenerator 
        userId={user?.id}
        sessionId={id || currentSession?.id}
        resumeData={resumeData}
        externalModalOpen={isPortfolioModalOpen}
        onModalClose={() => setIsPortfolioModalOpen(false)}
        onPortfolioCreated={(portfolio) => {
          setExistingPortfolio(portfolio);
          setIsPortfolioModalOpen(false);
        }}
      />
      
      {/* 삭제 확인 모달 */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="이력서 삭제"
        message={`"${sessionData?.name || resumeData?.name || '이름 없음'}님의 이력서"를 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없으며, 관련된 모든 데이터가 함께 삭제됩니다.`}
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />

    </div>
  );
};

// ResumePage 컴포넌트를 메모이제이션으로 최적화
const MemoizedResumePage = React.memo(ResumePage);

// 페이지 헤더 컴포넌트 (메모이제이션 최적화)
const ResumePageHeader = React.memo(({ sessionData, resumeData, actualProgress, onContinueConsulting }) => {
  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}. ${month}. ${day}.`;
  };

  return (
    <div className="section-container py-3">
      {/* 상단 헤더 영역 */}
      <div className="flex-between">
        <div>
          <h2 className="resume-header-title">
            <span className="resume-header-name">
              {sessionData?.name || resumeData.name || '이름 없음'}
            </span>
            <span className="resume-header-suffix">
              님의 이력서
            </span>
          </h2>
          <div>
            <div>
              {sessionData ? (
                <div className="resume-header-info">
                  <div className="resume-header-meta">
                    <span>
                      <span className="resume-header-label">생성일:</span>
                      <span className="resume-header-value">{formatDate(sessionData.created_at)}</span>
                    </span>
                    <span className="resume-header-bullet">•</span>
                    <span>
                      <span className="resume-header-label">진행률:</span>
                      <span className="resume-header-value resume-header-progress">{actualProgress}%</span>
                    </span>
                  </div>
                  <ProgressIndicator 
                    resumeData={resumeData}
                    size="sm"
                    showText={false}
                    className=""
                  />
                </div>
              ) : (
                <div className="resume-header-info">
                  <div className="resume-header-meta">
                    <span className="resume-header-label">AI 컨설팅으로 생성된 이력서를 편집하고 다운로드하세요</span>
                    <span className="resume-header-bullet">•</span>
                    <span>
                      <span className="resume-header-label">진행률:</span>
                      <span className="resume-header-value resume-header-progress">{actualProgress}%</span>
                    </span>
                  </div>
                  <ProgressIndicator 
                    resumeData={resumeData}
                    size="sm"
                    showText={false}
                    className=""
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <LatteButton
            onClick={onContinueConsulting}
            size="lg"
            variant="gradient"
            className="shadow-2xl rounded-2xl"
            title="AI와 계속 대화하며 이력서 개선"
          >
            <MessageCircle className="w-5 h-5" />
            AI 컨설팅 계속하기
          </LatteButton>
        </div>
      </div>
      
      {/* 빈 공간 - 진행률 바가 인라인으로 표시되므로 별도 영역 불필요 */}
    </div>
  );
});


export default MemoizedResumePage;
